import { Colors } from '../../../theme/Colors';
import { Text } from '../../display/Text';
import { Img } from '../../display/Img/Img';
import { LegacyColorMap } from '@/theme/LegacyColorMap';

import styles from './OneColumnBlock.module.scss';

type BackgroundColors =
  | 'cream'
  | 'neutral100'
  | 'acidWash'
  | 'neutral200'
  | 'lava10'
  | 'ocean10'
  | 'sun10'
  | 'white';

export type Props = {
  /**
   * Controls the background of the entire element.
   * Must be one of type `'cream' | 'neutral100' | 'acidWash' | 'neutral200' | 'lava10' | 'ocean10' | 'sun10' | 'white'`.
   */
  backgroundColor?: BackgroundColors;
  /**
   * Heading text content. Hidden when imageUrl is provided.
   */
  heading?: React.ReactNode;
  /**
   * Heading tag. Defines the HTML tag for the heading that component will render.
   */
  headingTag?: 'h1' | 'h2' | 'h3';
  /**
   * Text content displayed below the heading. Hidden when imageUrl is provided.
   */
  description?: React.ReactNode;
  /**
   * Image URL. Used to render an Img component.
   */
  imageUrl?: string;
  /**
   * Image alt text.
   */
  altText?: string;
  /**
   * Text textAlign options.
   */
  textAlign?: 'left' | 'right' | 'center' | 'inherit';
};

export const OneColumnBlock = ({
  backgroundColor,
  altText = '',
  headingTag = 'h3',
  heading,
  description,
  imageUrl,
  textAlign = 'center',
}: Props) => {
  const textMarginLeft =
    textAlign === 'center' || textAlign === 'right' ? 'auto' : 0;
  const textMarginRight = textAlign === 'center' ? 'auto' : 0;

  return (
    <div
      className={styles.root}
      data-zds
      style={{
        '--_zds-one-column-block-background-color': backgroundColor
          ? Colors[LegacyColorMap[backgroundColor]]
          : '',
      }}
    >
      <div className={styles.content}>
        {imageUrl ? (
          <Img borderRadius="none" src={imageUrl} alt={altText} />
        ) : (
          <div
            data-testid="Text"
            className={styles.text}
            data-zds
            style={{
              '--_zds-one-column-block-text-align': textAlign,
              '--_zds-one-column-block-margin-left': textMarginLeft,
              '--_zds-one-column-block-margin-right': textMarginRight,
            }}
          >
            <div className={styles.headingContainer} data-zds>
              <Text type="heading" color="GrayWarm10" tag={headingTag}>
                {heading}
              </Text>
            </div>
            {description && (
              <div className={styles.description} data-zds>
                {description}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
